import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { collection, doc, query, where } from 'firebase/firestore';
import { Globe2, Network, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import type { StoreDoc } from '@asm/shared';
import { db } from '@/lib/firebase';
import { api, callableMessage } from '@/lib/callables';
import { useLiveDoc } from '@/lib/hooks';
import { useSession } from '@/auth/AuthProvider';
import { cn, timeAgo } from '@/lib/utils';

/** ISO-3166 alpha-2 → regional-indicator flag. */
function flagEmoji(countryCode: string): string {
  const cc = countryCode.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '🌐';
  return String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

/**
 * Which address this store's App Store Connect calls leave from.
 *
 * Every store shares the server's egress unless it has a dedicated IP, so this
 * makes the separation visible: you can see at a glance that two client accounts
 * are not reaching Apple from the same place.
 */
export function EgressBadge() {
  const { sid } = useParams();
  const { user } = useSession();
  const [open, setOpen] = useState(false);
  const storeRef = useMemo(() => (sid ? doc(db, 'stores', sid) : null), [sid]);
  const store = useLiveDoc<StoreDoc>(storeRef);

  const test = useMutation({
    mutationFn: (opts: { silent?: boolean } = {}) =>
      api.storeProxyTest({ storeId: sid! }).then((res) => ({ ...res, silent: opts.silent })),
    // The badge resolves itself on load; only a deliberate re-check should shout.
    onSuccess: (res) => {
      if (res.silent) return;
      toast.success(`Calling Apple from ${res.ip}`, {
        description: res.proxied
          ? `${res.country ?? 'Dedicated'} address — not shared with your other stores.`
          : 'Shared server address — this store has no dedicated IP yet.',
      });
    },
    onError: (err, opts) => {
      if (opts?.silent) return;
      toast.error('Egress check failed', { description: callableMessage(err) });
    },
  });

  const proxy = store.data?.proxy;
  const check = proxy?.lastCheck;
  const proxied = !!proxy?.enabled;
  const isAdmin = user?.role === 'admin';
  // Re-resolve when we've never checked, or the reading has aged out. A store that
  // egresses directly still has a real address — showing "shared IP" with no number
  // tells you nothing about where the calls actually came from.
  const stale = !check?.at || Date.now() - check.at.toMillis() > 60 * 60_000;
  const attempted = useRef<string | null>(null);
  useEffect(() => {
    if (!sid || !store.data || !isAdmin || !stale) return;
    if (attempted.current === sid) return;
    attempted.current = sid;
    test.mutate({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid, store.data, isAdmin, stale]);

  if (!sid || !store.data) return null;
  const flag = check?.countryCode ? flagEmoji(check.countryCode) : proxy?.country ? flagEmoji(proxy.country) : '🌐';
  const canTest = user?.role === 'admin';

  return (
    <div className="pointer-events-none fixed bottom-3 left-3 z-40 hidden lg:block lg:left-[calc(14rem+0.75rem)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'pointer-events-auto flex items-center gap-1.5 rounded-full border bg-card/95 px-2.5 py-1 text-[11px] shadow-card backdrop-blur transition-colors hover:bg-card',
          proxied ? 'border-success/40' : 'border-border',
        )}
        title={proxied ? 'This store calls Apple from its own dedicated address' : 'This store shares the server’s outbound address'}
      >
        <span className="text-[13px] leading-none">{flag}</span>
        <span className="font-mono tabular-nums text-foreground">
          {check?.ok && check.ip ? check.ip : test.isPending ? 'checking…' : '—'}
        </span>
        <span className="text-muted-foreground">{proxied ? 'dedicated' : 'shared'}</span>
        <span className={cn('size-1.5 rounded-full', proxied ? 'bg-success' : 'bg-muted-foreground/40')} />
      </button>

      {open && (
        <div className="pointer-events-auto mt-2 w-64 rounded-xl border bg-card p-3 text-[12px] shadow-pop">
          <div className="flex items-center gap-2">
            <Network className="size-3.5 text-muted-foreground" />
            <span className="font-medium">Outbound to App Store Connect</span>
          </div>
          <dl className="mt-2 space-y-1.5 text-muted-foreground">
            <div className="flex justify-between gap-2">
              <dt>Store</dt>
              <dd className="truncate font-medium text-foreground">{store.data.name}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt>Address</dt>
              <dd className="font-mono text-foreground">{check?.ip || '—'}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt>Location</dt>
              <dd className="text-foreground">
                {flag} {check?.country ?? (proxy?.country ? proxy.country.toUpperCase() : 'unknown')}
                {check?.city ? ` · ${check.city}` : ''}
              </dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt>Mode</dt>
              <dd className={cn('font-medium', proxied ? 'text-success' : 'text-foreground')}>
                {proxied ? 'Dedicated' : 'Shared server IP'}
              </dd>
            </div>
            {check?.at && (
              <div className="flex justify-between gap-2">
                <dt>Checked</dt>
                <dd>{timeAgo(check.at)}</dd>
              </div>
            )}
          </dl>
          {!proxied && (
            <p className="mt-2 flex items-start gap-1.5 text-[11px] text-muted-foreground">
              <Globe2 className="mt-0.5 size-3 shrink-0" />
              Shared with your other stores — turn on a dedicated IP in store settings.
            </p>
          )}
          {canTest && (
            <button
              type="button"
              onClick={() => test.mutate({})}
              disabled={test.isPending}
              className="mt-2.5 flex w-full items-center justify-center gap-1.5 rounded-md border px-2 py-1 text-[11px] font-medium hover:bg-muted disabled:opacity-60"
            >
              <RefreshCw className={cn('size-3', test.isPending && 'animate-spin')} />
              {test.isPending ? 'Checking…' : 'Re-check now'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
